import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Card, message, Space, Switch, Typography } from "antd";
import { ToolOutlined } from "@ant-design/icons";
import useMaintenanceService from "../../services/SuperAdmin/maintenanceService";
import { setMaintenanceEnabled } from "../../state/slices/generalSlice";

const { Title, Paragraph } = Typography;

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  if (typeof error === "string") return error;
  if (typeof error?.message === "string") return error.message;

  return fallback;
};

const MaintenanceSettings = () => {
  const dispatch = useDispatch();
  const maintenanceEnabled = useSelector(
    (state) => state.generalSlice.maintenanceEnabled,
  );
  const {
    getMaintenanceStatus,
    statusResponse,
    toggleMaintenance,
    toggleLoading,
    toggleError,
  } = useMaintenanceService();

  useEffect(() => {
    getMaintenanceStatus().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (statusResponse) {
      dispatch(setMaintenanceEnabled(Boolean(statusResponse?.data?.is_enabled)));
    }
  }, [statusResponse, dispatch]);

  const handleToggle = async (checked) => {
    try {
      await toggleMaintenance(checked);
      dispatch(setMaintenanceEnabled(checked));
      message.success(
        checked
          ? "Maintenance mode enabled. Only Super Admin and Positions Admin can access PIVOT."
          : "Maintenance mode disabled. PIVOT is accessible to everyone again.",
      );
    } catch (error) {
      message.error(getErrorMessage(error, "Failed to update maintenance mode."));
    }
  };

  return (
    <Card>
      <Title level={4}>
        <ToolOutlined /> Maintenance Mode
      </Title>
      <Paragraph type="secondary">
        When enabled, everyone except Super Admin and Positions Admin will be
        shown a maintenance message and won't be able to access PIVOT.
      </Paragraph>

      {toggleError && (
        <Alert
          type="error"
          showIcon
          message="Unable to update maintenance mode"
          description={getErrorMessage(
            toggleError,
            "Failed to update maintenance mode.",
          )}
          style={{ marginBottom: 16 }}
        />
      )}

      <Space size="middle">
        <Switch
          checked={maintenanceEnabled}
          loading={toggleLoading}
          onChange={handleToggle}
        />
        <span>
          Maintenance mode is currently{" "}
          <strong>{maintenanceEnabled ? "ON" : "OFF"}</strong>
        </span>
      </Space>
    </Card>
  );
};

export default MaintenanceSettings;
